import React, { useEffect, useState } from 'react';
import { SalvButton, SalvCheckboxGroup, SalvGroupedCheckbox } from '@salvapp/ui-components';
import { conversationCategoryApi, userAlertRfiMessageNotificationApi } from '../../apis';
import LoadingOverlay from '../../feedback/LoadingOverlay';
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { BridgeApiConversationCategory, InternalApiUserAlertRfiMessageNotifications } from '../../api-internal';

const validationSchema = Yup.object().shape({
  categoryConfiguration: Yup.array()
    .of(
      Yup.object().shape({
        category: Yup.string().required(),
        notifyNewMessage: Yup.boolean().required(),
        notifyReply: Yup.boolean().required(),
      })
    )
    .required(),
});

export interface NotificationSetting {
  category: string;
  notifyNewMessage: boolean;
  notifyReply: boolean;
}
export interface NotificationSettingsFormValues {
  categoryConfiguration: NotificationSetting[];
}

function categoryIdsToDropdownOption(currentCategories: InternalApiUserAlertRfiMessageNotifications | undefined) {
  return {
    categoryConfiguration: (currentCategories?.categoryConfigurations || []).map(
      (category) =>
        ({
          category: category.categoryId,
          notifyNewMessage: category.notifyNewMessage,
          notifyReply: category.notifyReply,
        }) as NotificationSetting
    ),
  };
}

export const NotificationSettings = (): React.JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [categories, setCategories] = useState<BridgeApiConversationCategory[]>([]);
  const [currentCategories, setCurrentCategories] = useState<InternalApiUserAlertRfiMessageNotifications>();
  async function fetchConversationCategories() {
    const catPromise = conversationCategoryApi.getConversationCategoriesForOrganisation();
    const notPromise = userAlertRfiMessageNotificationApi.findAlertRfiMessageNotifications();
    setCategories(await catPromise);
    setCurrentCategories(await notPromise);
    setLoading(false);
  }

  useEffect(() => {
    fetchConversationCategories();
  }, []);

  const initialValues: NotificationSettingsFormValues = categoryIdsToDropdownOption(currentCategories);

  const submit = async (values: NotificationSettingsFormValues, { resetForm, setSubmitting }: FormikHelpers<any>) => {
    setSubmitting(true);
    const categoryConfigurations = values.categoryConfiguration.map((category) => ({
      categoryId: category.category,
      notifyNewMessage: category.notifyNewMessage,
      notifyReply: category.notifyReply,
    }));
    const newCategories = await userAlertRfiMessageNotificationApi.updateAlertRfiMessageNotifications({
      internalApiUpdateUserAlertRfiMessageNotifications: {
        categoryIds: values.categoryConfiguration.map((category) => category.category),
        categoryConfigurations: categoryConfigurations,
      },
    });
    setCurrentCategories(newCategories);
    resetForm({
      values: initialValues,
      isSubmitting: false,
      isValidating: false,
    });
    setSubmitting(false);
    setEditing(false);
  };

  return (
    <LoadingOverlay loading={loading}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, resetForm }: FormikProps<any>) => {
          return (
            <Form>
              <p>{'Get an email when there’s a new message or reply in the categories you follow.'}</p>
              <p>Notifications for alerts assigned to you are always enabled.</p>
              {categories.map((category) => (
                <FieldArray
                  key={category.handle}
                  name='categoryConfiguration'
                  render={(arrayHelpers) => {
                    const configIndex = values.categoryConfiguration.findIndex(
                      (c: { category: string | undefined }) => c.category === category.handle
                    );
                    const config = values.categoryConfiguration[configIndex];

                    return (
                      <SalvCheckboxGroup
                        label={category.name}
                        onChange={(fieldValues) => {
                          const newConfig = {
                            category: category.handle,
                            notifyNewMessage: fieldValues.includes('notifyNewMessage'),
                            notifyReply: fieldValues.includes('notifyReply'),
                          };

                          if (configIndex !== -1) {
                            arrayHelpers.replace(configIndex, newConfig);
                          } else {
                            arrayHelpers.push(newConfig);
                          }
                        }}
                        value={{
                          notifyNewMessage: config?.notifyNewMessage || false,
                          notifyReply: config?.notifyReply || false,
                        }}
                      >
                        <SalvGroupedCheckbox
                          label='Notify for New Messages'
                          value='notifyNewMessage'
                          disabled={!editing || isSubmitting}
                        />
                        <SalvGroupedCheckbox
                          label='Notify for Replies'
                          value='notifyReply'
                          disabled={!editing || isSubmitting}
                        />
                      </SalvCheckboxGroup>
                    );
                  }}
                />
              ))}
              {editing ? (
                <div className='m-t-8'>
                  <SalvButton
                    type='submit'
                    variant='primary'
                    disabled={isSubmitting}
                    label='Confirm'
                    className='m-r-8'
                  />
                  <SalvButton
                    variant='muted'
                    disabled={isSubmitting}
                    label='Cancel'
                    onClick={() => {
                      resetForm({
                        values: initialValues,
                        isSubmitting: false,
                        isValidating: false,
                      });
                      setEditing(false);
                    }}
                  />
                </div>
              ) : (
                <SalvButton
                  variant='muted'
                  onClick={() => setEditing(true)}
                  disabled={isSubmitting}
                  label='Edit'
                  className='m-t-8'
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </LoadingOverlay>
  );
};

export default NotificationSettings;
